import * as Headless from '@headlessui/react';
import clsx from 'clsx';
import { useState } from 'react';

export type ComboboxProps<T> = {
  options: T[];
  displayValue: (value: T | null) => string | undefined;
  filter?: (value: T, query: string) => boolean;
  className?: string;
  placeholder?: string;
  autoFocus?: boolean;
  'aria-label'?: string;
  children: (value: NonNullable<T>) => React.ReactElement;
} & Omit<Headless.ComboboxProps<T, false>, 'as' | 'multiple' | 'children'> & { anchor?: 'top' | 'bottom' };

/**
 * Combobox component for selecting options from a list.
 *
 * TESTING NOTE: This component uses a virtualized list of options for performance.
 * You may need to mock the getBoundingClientRect and offsetWidth/Height methods in your tests.
 * so that vitualization can compute the correct dimensions.
 * @param options
 * @param displayValue
 * @param filter
 * @param anchor
 * @param className
 * @param placeholder
 * @param autoFocus
 * @param ariaLabel
 * @param children
 * @param props
 * @constructor
 */
export function Combobox<T>({
  options,
  displayValue,
  filter,
  anchor = 'bottom',
  className,
  placeholder,
  autoFocus,
  'aria-label': ariaLabel,
  children,
  ...props
}: ComboboxProps<T>) {
  const [query, setQuery] = useState('');

  const filteredOptions =
    query === ''
      ? options
      : options.filter((option) =>
          filter ? filter(option, query) : displayValue(option)?.toLowerCase().includes(query.toLowerCase()),
        );

  return (
    <Headless.Combobox
      {...props}
      immediate
      multiple={false}
      virtual={{ options: filteredOptions }}
      onClose={() => {
        props.onClose?.();
        setQuery('');
      }}
    >
      <span
        data-slot="control"
        className={clsx([
          className,
          // Basic layout
          'relative block w-full',
          // Background color + shadow applied to inset pseudo element, so shadow blends with border in light mode
          'before:absolute before:inset-px before:rounded-[calc(var(--radius-lg)-1px)] before:bg-white before:shadow-sm',
          // Background color is moved to control and shadow is removed in dark mode so hide `before` pseudo
          'dark:before:hidden',
          // Focus ring
          'sm:focus-within:after:ring-bright-blue-7 after:pointer-events-none after:absolute after:inset-0 after:rounded-lg after:ring-inset after:ring-transparent sm:focus-within:after:ring-2',
          // Disabled state
          'has-[[data-disabled]]:before:bg-gray-12/5 has-[[data-disabled]]:opacity-50 has-[[data-disabled]]:before:shadow-none',
          // Invalid state
          'has-[[data-invalid]]:before:shadow-red-500/10',
        ])}
      >
        <Headless.ComboboxInput
          autoFocus={autoFocus}
          data-slot="control"
          aria-label={ariaLabel}
          displayValue={(option: T) => displayValue(option) ?? ''}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={placeholder}
          className={clsx([
            className,
            // Basic layout
            'relative block w-full appearance-none rounded-lg px-[calc(theme(spacing[3.5])-1px)] py-[calc(theme(spacing[2.5])-1px)] sm:px-[calc(theme(spacing[3])-1px)] sm:py-[calc(theme(spacing[1.5])-1px)]',
            // Typography
            'text-gray-12 placeholder:text-gray-6 text-base/6 sm:text-sm/6 dark:text-white',
            // Border
            'border-gray-12/10 data-[hover]:border-gray-12/20 border dark:border-white/10 dark:data-[hover]:border-white/20',
            // Invalid state
            'data-[invalid]:border-red-500 data-[invalid]:data-[hover]:border-red-500 dark:data-[invalid]:border-red-500 dark:data-[invalid]:data-[hover]:border-red-500',
            // Disabled state
            'data-[disabled]:border-gray-12/20 dark:data-[disabled]:bg-white/2.5 dark:data-[disabled]:border-white/15 dark:data-[hover]:data-[disabled]:border-white/15',
            // System icons
            'dark:scheme-dark',
          ])}
        />
        <Headless.ComboboxButton className="group absolute inset-y-0 right-0 flex items-center px-2">
          <svg
            className="stroke-gray-6 group-[[data-disabled]]:stroke-gray-8 group-[[data-hover]]:stroke-gray-9 dark:stroke-gray-4 dark:group-[[data-hover]]:stroke-gray-3 h-5 w-5 sm:h-4 sm:w-4 forced-colors:stroke-[CanvasText]"
            viewBox="0 0 16 16"
            aria-hidden="true"
            fill="none"
          >
            <path d="M5.75 10.75L8 13L10.25 10.75" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
            <path d="M10.25 5.25L8 3L5.75 5.25" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </Headless.ComboboxButton>
      </span>
      <Headless.ComboboxOptions
        transition
        anchor={anchor}
        className={clsx(
          // Anchor positioning --anchor-max-height is an undocumented css variable, but is used by Headless UI to set the max height of the options list
          '[--anchor-gap:theme(spacing[2])] [--anchor-max-height:theme(spacing[60])] [--anchor-padding:theme(spacing[4])] sm:data-[anchor~=start]:[--anchor-offset:-4px]',
          // Base styles,
          'isolate max-h-60 min-w-[calc(var(--input-width)+8px)] select-none scroll-py-1 rounded-xl p-1 empty:invisible',
          // Invisible border that is only visible in `forced-colors` mode for accessibility purposes
          'focus:outline-hidden outline outline-transparent',
          // Handle scrolling when menu won't fit in viewport
          'overflow-y-scroll overscroll-contain',
          // Popover background
          'dark:bg-gray-10/75 bg-white/75 backdrop-blur-xl',
          // Shadows
          'ring-gray-12/10 shadow-lg ring-1 dark:ring-inset dark:ring-white/10',
          // Transitions
          'transition-opacity duration-100 ease-in data-[transition]:pointer-events-none data-[closed]:data-[leave]:opacity-0',
          // Z Index needed for showing options on dialogs
          'z-50',
        )}
      >
        {({ option }) => children(option)}
      </Headless.ComboboxOptions>
    </Headless.Combobox>
  );
}

export function ComboboxOption<T>({
  children,
  className,
  ...props
}: { className?: string; children?: React.ReactNode } & Omit<
  Headless.ComboboxOptionProps<'div', T>,
  'as' | 'className'
>) {
  let sharedClasses = clsx(
    // Base
    'flex min-w-0 items-center',
    // Icons
    '*:data-[slot=icon]:h-5 *:data-[slot=icon]:w-5 *:data-[slot=icon]:shrink-0 sm:*:data-[slot=icon]:h-4 sm:*:data-[slot=icon]:w-4',
    '*:data-[slot=icon]:text-gray-6 dark:*:data-[slot=icon]:text-gray-4 group-[[data-focus]]/option:*:data-[slot=icon]:text-white',
    'forced-colors:*:data-[slot=icon]:text-[CanvasText] forced-colors:group-[[data-focus]]/option:*:data-[slot=icon]:text-[Canvas]',
    // Avatars
    '*:data-[slot=avatar]:-mx-0.5 *:data-[slot=avatar]:h-6 *:data-[slot=avatar]:w-6 sm:*:data-[slot=avatar]:h-5 sm:*:data-[slot=avatar]:w-5',
  );

  return (
    <Headless.ComboboxOption
      {...props}
      className={clsx(
        // Basic layout
        'group/option grid w-full cursor-default grid-cols-[1fr_theme(spacing.5)] items-baseline gap-x-2 rounded-lg py-2.5 pl-3.5 pr-2 sm:grid-cols-[1fr_theme(spacing.4)] sm:py-1.5 sm:pl-3 sm:pr-2',
        // Typography
        'text-gray-12 text-base/6 sm:text-sm/6 dark:text-white forced-colors:text-[CanvasText]',
        // Focus
        'outline-hidden data-[focus]:bg-bright-blue-7 data-[focus]:text-white',
        // Forced colors mode
        'forced-color-adjust-none forced-colors:data-[focus]:bg-[Highlight] forced-colors:data-[focus]:text-[HighlightText]',
        // Disabled
        'data-[disabled]:opacity-50',
      )}
    >
      <span className={clsx(className, sharedClasses)}>{children}</span>
      <svg
        className="relative col-start-2 hidden h-5 w-5 self-center stroke-current group-[[data-selected]]/option:inline sm:h-4 sm:w-4"
        viewBox="0 0 16 16"
        fill="none"
        aria-hidden="true"
      >
        <path d="M4 8.5l3 3L12 4" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </Headless.ComboboxOption>
  );
}

export function ComboboxLabel({ className, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return <span {...props} className={clsx(className, 'ml-2.5 truncate first:ml-0 sm:ml-2 sm:first:ml-0')} />;
}

export function ComboboxDescription({ className, children, ...props }: React.ComponentPropsWithoutRef<'span'>) {
  return (
    <span
      {...props}
      className={clsx(
        className,
        'text-gray-6 dark:text-gray-4 flex flex-1 overflow-hidden before:w-2 before:min-w-0 before:shrink group-[[data-focus]]/option:text-white',
      )}
    >
      <span className="flex-1 truncate">{children}</span>
    </span>
  );
}
